// Quantum hypercube router — an exact statevector simulator (no qiskit needed),
// ported from the QuantumHypercubeRouter Python class. Runs fully offline.
// Qubit 0 is the least-significant bit, matching qiskit's ordering.

export type C = [number, number];
type Gate = { name: string; qubits: number[]; theta?: number };

export class QuantumHypercubeRouter {
  readonly numQubits: number;
  state: C[];
  gates: Gate[] = [];

  constructor(numQubits = 4) {
    this.numQubits = Math.max(1, Math.min(10, Math.floor(numQubits)));
    this.state = Array.from({ length: 1 << this.numQubits }, (_, i) => (i === 0 ? [1, 0] : [0, 0]) as C);
  }

  private single(q: number, m: [C, C, C, C]) {
    const bit = 1 << q;
    for (let i = 0; i < this.state.length; i++) {
      if (i & bit) continue;
      const a = this.state[i], b = this.state[i | bit];
      const mul = (x: C, y: C): C => [x[0] * y[0] - x[1] * y[1], x[0] * y[1] + x[1] * y[0]];
      const add = (x: C, y: C): C => [x[0] + y[0], x[1] + y[1]];
      this.state[i] = add(mul(m[0], a), mul(m[1], b));
      this.state[i | bit] = add(mul(m[2], a), mul(m[3], b));
    }
  }

  h(q: number) { const s = Math.SQRT1_2; this.single(q, [[s, 0], [s, 0], [s, 0], [-s, 0]]); this.gates.push({ name: "H", qubits: [q] }); }
  rx(t: number, q: number) { const c = Math.cos(t / 2), s = Math.sin(t / 2); this.single(q, [[c, 0], [0, -s], [0, -s], [c, 0]]); this.gates.push({ name: "RX", qubits: [q], theta: t }); }
  ry(t: number, q: number) { const c = Math.cos(t / 2), s = Math.sin(t / 2); this.single(q, [[c, 0], [-s, 0], [s, 0], [c, 0]]); this.gates.push({ name: "RY", qubits: [q], theta: t }); }
  cx(c: number, t: number) {
    const cb = 1 << c, tb = 1 << t;
    for (let i = 0; i < this.state.length; i++) {
      if (i & cb && !(i & tb)) { const tmp = this.state[i]; this.state[i] = this.state[i | tb]; this.state[i | tb] = tmp; }
    }
    this.gates.push({ name: "CX", qubits: [c, t] });
  }

  initializeHypercubeSuperposition() { for (let q = 0; q < this.numQubits; q++) this.h(q); }

  applyPolymorphicTransformation(params: number[]) {
    params.slice(0, this.numQubits).forEach((t, i) => { this.rx(t, i); this.ry(t / 2, i); });
    for (let i = 0; i < this.numQubits - 1; i++) this.cx(i, i + 1);
    if (this.numQubits > 1) this.cx(this.numQubits - 1, 0);
  }

  probabilities(): Array<{ state: string; p: number }> {
    return this.state.map((a, i) => ({ state: i.toString(2).padStart(this.numQubits, "0"), p: a[0] ** 2 + a[1] ** 2 }));
  }

  draw(): string {
    return this.gates.map((g) => `${g.name}${g.theta !== undefined ? `(${g.theta.toFixed(3)})` : ""} q${g.qubits.join(",q")}`).join("  →  ");
  }
}

export const DEMO_ANGLES = [Math.PI / 4, Math.PI / 2, Math.PI / 3, Math.PI / 6];

export function runDemo(angles = DEMO_ANGLES) {
  const r = new QuantumHypercubeRouter(4);
  r.initializeHypercubeSuperposition();
  r.applyPolymorphicTransformation(angles);
  const probs = r.probabilities();
  const entropy = -probs.reduce((s, x) => s + (x.p > 1e-12 ? x.p * Math.log2(x.p) : 0), 0);
  return { router: r, probs, entropy };
}

/** One-paragraph plain summary Mrs DNA can speak. */
export function quantumSummary(): string {
  const { probs, entropy } = runDemo();
  const top = [...probs].sort((a, b) => b.p - a.p).slice(0, 3);
  return `I ran the 4-qubit hypercube router: all 16 vertices in superposition, morphed by RX/RY rotations and a closed ring of CNOTs. The most likely nodes are ${top.map((t) => `|${t.state}⟩ at ${(t.p * 100).toFixed(1)}%`).join(", ")}, with ${entropy.toFixed(2)} of 4 possible bits of entropy.`;
}

export function looksQuantumQuestion(text: string): boolean {
  return /\b(quantum|qubits?|hypercube|superposition|entangle\w*|statevector|circuit|probabilit\w*)\b/i.test(text);
}

/** Full plain-English explanation of the circuit and its results, for chat. */
export function quantumExplanation(): string {
  const { probs, entropy } = runDemo();
  const sorted = [...probs].sort((a, b) => b.p - a.p);
  const top = sorted.slice(0, 4), low = sorted.slice(-2);
  const near = probs.filter((x) => x.p < 1e-6).length;
  return [
    "My quantum hypercube router (4 qubits, exact statevector simulation, run on this device):",
    "- The circuit: a Hadamard on each qubit puts all 16 vertices of a 4D hypercube (|0000⟩…|1111⟩) into equal superposition, 6.25% each.",
    "- Polymorphic step: each qubit i is rotated RX(θi) then RY(θi/2) with θ = π/4, π/2, π/3, π/6 — this bends the equal spread into an uneven one, a different shape for each set of angles.",
    "- Entanglement: CNOTs q0→q1→q2→q3 and a closing q3→q0 join neighbouring corners into a ring, so the qubits can no longer be described one at a time.",
    `- Result: most likely vertices ${top.map((t) => `|${t.state}⟩ ${(t.p * 100).toFixed(2)}%`).join(", ")}; least likely ${low.map((t) => `|${t.state}⟩ ${(t.p * 100).toFixed(2)}%`).join(", ")}${near ? `; ${near} vertices are almost impossible` : ""}.`,
    `- Spread: ${entropy.toFixed(3)} bits of Shannon entropy out of a maximum 4 — 4 would mean perfectly even, lower means the rotations have steered probability toward certain corners.`,
    "- Meaning: it is a mathematical simulation of a quantum circuit, not a physical quantum computer; states are written with qubit 0 as the rightmost digit, as qiskit does.",
  ].join("\n");
}

/**
 * Quantum hypercube view of a mission: each exhibit's seal sets a rotation
 * angle, the router runs, and the most likely vertices are mapped back to
 * exhibits so the mission explanation shows which papers the circuit weights.
 */
export function missionQuantum(exhibits: Array<{ ref: string; seal: string }>): string {
  if (!exhibits.length) return "";
  const angles = [0, 1, 2, 3].map((i) => {
    const e = exhibits[i % exhibits.length];
    const h = parseInt((e.seal || "0").slice(i * 4, i * 4 + 6) || "0", 16) || 1;
    return ((h % 6283) / 1000);
  });
  const { router, probs, entropy } = runDemo(angles);
  const top = [...probs].map((p, i) => ({ ...p, i })).sort((a, b) => b.p - a.p).slice(0, 4);
  return [
    "Quantum hypercube view of this mission (4 qubits, exact simulation on this device):",
    `- Angles taken from the exhibit seals: ${angles.map((a) => a.toFixed(3)).join(", ")} rad.`,
    `- Circuit: ${router.draw().split("  →  ").length} gates — Hadamards, RX/RY rotations and a closed CNOT ring.`,
    ...top.map((t) => `- |${t.state}⟩ ${(t.p * 100).toFixed(2)}% → ${exhibits[t.i % exhibits.length].ref}`),
    `- Spread: ${entropy.toFixed(3)} of 4 bits. Lower means the seals steer weight onto fewer exhibits.`,
    "- This is a mathematical lens on the file, not evidence in itself; the proof stays in the cited exhibits.",
  ].join("\n");
}

/**
 * Run a circuit the user writes in chat, e.g. "run H q0, CX q0 q1, RY 1.2 q2
 * on 3 qubits". Returns exact probabilities plus 1024 simulated measurement
 * shots, the way a real quantum computer reports results.
 */
export function runChatCircuit(text: string): string | null {
  const gateRe = /\b(H|X|Y|Z|RX|RY|CX|CNOT)\s*(\(?-?[\d.]+(?:\s*\*?\s*pi)?\)?)?\s*q?(\d)(?:\s*[,→>-]*\s*q?(\d))?/gi;
  if (!/\b(run|simulate|build|apply)\b/i.test(text) || !/\bq\d\b/i.test(text)) return null;
  const gates: Array<{ g: string; a?: number; q: number; t?: number }> = [];
  let m: RegExpExecArray | null;
  while ((m = gateRe.exec(text))) {
    const g = m[1].toUpperCase() === "CNOT" ? "CX" : m[1].toUpperCase();
    let a: number | undefined;
    if (m[2]) { const raw = m[2].replace(/[()\s*]/g, ""); a = raw.endsWith("pi") ? parseFloat(raw) * Math.PI : parseFloat(raw); }
    gates.push({ g, a, q: +m[3], t: m[4] !== undefined ? +m[4] : undefined });
  }
  if (!gates.length) return null;
  const maxQ = Math.max(...gates.flatMap((x) => [x.q, x.t ?? 0]));
  const nMatch = /(\d+)\s*qubits?/i.exec(text);
  const n = Math.min(8, Math.max(maxQ + 1, nMatch ? +nMatch[1] : 1));
  const r = new QuantumHypercubeRouter(n);
  for (const x of gates) {
    if (x.g === "H") r.h(x.q);
    else if (x.g === "X") r.rx(Math.PI, x.q);
    else if (x.g === "Y") r.ry(Math.PI, x.q);
    else if (x.g === "Z") { r.h(x.q); r.rx(Math.PI, x.q); r.h(x.q); }
    else if (x.g === "RX") r.rx(x.a ?? Math.PI / 2, x.q);
    else if (x.g === "RY") r.ry(x.a ?? Math.PI / 2, x.q);
    else if (x.g === "CX" && x.t !== undefined && x.t !== x.q) r.cx(x.q, x.t);
  }
  const probs = r.probabilities().filter((p) => p.p > 1e-9).sort((a, b) => b.p - a.p);
  const shots = 1024; const counts: Record<string, number> = {};
  for (let s = 0; s < shots; s++) {
    let u = Math.random(), pick = probs[probs.length - 1].state;
    for (const p of probs) { if ((u -= p.p) <= 0) { pick = p.state; break; } }
    counts[pick] = (counts[pick] ?? 0) + 1;
  }
  const entropy = -probs.reduce((s, x) => s + x.p * Math.log2(x.p), 0);
  return [
    `I ran your circuit on ${n} qubit${n > 1 ? "s" : ""} (exact statevector simulation on this device, then ${shots} measurement shots like a real quantum computer):`,
    `- Circuit: ${r.draw() || "no gates"}`,
    ...probs.slice(0, 8).map((p) => `- |${p.state}⟩ exact ${(p.p * 100).toFixed(2)}% · measured ${counts[p.state] ?? 0}/${shots}`),
    `- Entropy ${entropy.toFixed(3)} of ${n} bits. Shot counts wobble around the exact values, just as real hardware results do (real hardware also adds noise).`,
    "- Qubit 0 is the rightmost digit, as in qiskit.",
  ].join("\n");
}
